import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Add, Remove } from '@mui/icons-material';
import EmptyState from '../components/EmptyState';
import { cmsService, FAQItem } from '../services/cms';

const FAQPage: React.FC = () => {
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const isTr = language === 'tr';
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const [faqItems, setFaqItems] = useState<FAQItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadFAQs();
  }, []);

  const loadFAQs = async () => {
    try {
      const data = await cmsService.getAllFAQItems(false);
      setFaqItems(data);
    } catch (error) {
      console.error('Failed to load FAQs:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  const categories = Array.from(new Set(faqItems.map(item => item.category)));

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      <main className="max-w-5xl mx-auto px-6 md:px-10 pt-24 md:pt-32 pb-16 md:pb-24">
        {/* Section header */}
        <div className="flex items-baseline justify-between mb-12">
          <span className="eyebrow"><span className="text-accent">/01</span> · {isTr ? 'SSS' : 'FAQ'}</span>
          <span className="eyebrow-mute hidden md:inline">{isTr ? 'CEVAP DEFTERİ' : 'ANSWER LEDGER'}</span>
        </div>

        <div className="max-w-3xl mb-16">
          <h1 className="display-h display-lg mb-6">
            {isTr ? <>Aklında <em>soru var.</em></> : <>You have <em>questions.</em></>}
          </h1>
          <p className="font-prose text-[17px] leading-relaxed text-ink-dim max-w-2xl">
            {t.faqSubtitle}
          </p>
        </div>

        {loading ? (
          <div className="py-16 border-t border-hairline">
            <span className="eyebrow-mute">{isTr ? 'YÜKLENİYOR…' : 'LOADING…'}</span>
          </div>
        ) : faqItems.length === 0 ? (
          <EmptyState
            title={isTr ? 'Yakında sorular eklenecek' : 'FAQs coming soon'}
            description={isTr
              ? 'Sık sorulan sorular burada toplanacak. Bu arada bizimle iletişime geçebilirsin.'
              : 'Common questions will be collected here. In the meantime, feel free to reach out.'}
            actionLabel={isTr ? 'İletişim' : 'Contact'}
            onAction={() => navigate('/contact')}
          />
        ) : (
          <>
            <div className="space-y-16">
              {categories.map((category, ci) => {
                const categoryItems = faqItems.filter(item => item.category === category);
                return (
                  <div key={category}>
                    <div className="flex items-baseline justify-between mb-6">
                      <span className="eyebrow"><span className="text-accent">/{String(ci + 1).padStart(2, '0')}</span> · {category.toUpperCase()}</span>
                      <span className="eyebrow-mute font-mono">{categoryItems.length} {isTr ? 'BAŞLIK' : 'ITEMS'}</span>
                    </div>
                    <div className="border-t border-hairline">
                      {categoryItems.map((item) => {
                        const globalIndex = faqItems.indexOf(item);
                        const isOpen = openIndex === globalIndex;
                        return (
                          <div
                            key={item.id}
                            className={`border-b border-hairline transition-colors ${isOpen ? 'bg-accent-soft' : ''}`}
                          >
                            <button
                              onClick={() => toggleFAQ(globalIndex)}
                              className="w-full px-1 py-6 flex items-start justify-between gap-6 text-left group"
                            >
                              <span className="font-serif italic text-xl md:text-2xl text-ink leading-snug pr-4">
                                {isTr ? item.question_tr : item.question_en}
                              </span>
                              <span className={`shrink-0 mt-1 w-7 h-7 border flex items-center justify-center transition-colors ${
                                isOpen ? 'border-accent text-accent' : 'border-hairline text-ink-dim group-hover:border-accent group-hover:text-accent'
                              }`}>
                                {isOpen ? <Remove sx={{ fontSize: 14 }} /> : <Add sx={{ fontSize: 14 }} />}
                              </span>
                            </button>
                            {isOpen && (
                              <div className="px-1 pb-8 pt-2 max-w-3xl">
                                <p className="font-prose text-[15px] leading-relaxed text-ink-dim whitespace-pre-line">
                                  {isTr ? item.answer_tr : item.answer_en}
                                </p>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* CTA */}
            <div className="text-center mt-24 pt-16 border-t border-hairline max-w-2xl mx-auto">
              <span className="eyebrow mb-4 block">{isTr ? 'BAŞKA SORU?' : 'ANOTHER QUESTION?'}</span>
              <h2 className="display-h display-md mb-6">
                {isTr ? <>Yine de bize <em>yaz.</em></> : <>Just <em>write to us.</em></>}
              </h2>
              <button onClick={() => navigate('/contact')} className="btn-accent">
                {isTr ? 'İLETİŞİME GEÇ' : 'GET IN TOUCH'}
                <span aria-hidden>→</span>
              </button>
            </div>
          </>
        )}
      </main>

      <Footer />
    </div>
  );
};

export default FAQPage;
